import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck, faCircleExclamation, faCircleInfo,
    faCircleMinus, faAngleRight, faAngleDown, faBullseye,
    faFlask, faInfo, faInfoCircle, faDna,
    faHandsBubbles,
    faNotesMedical
} from '@fortawesome/free-solid-svg-icons';
import './StepStyles.css';
import { 
    WgsAnalysisCard,
    GutHealthCard,
    ProteomicsCard,
    OralHealthCard,
    TranscriptomeCard,
    MetabolomeCard,
    MetabolomeUrineCard,
    MetabolomeLipidsCard,
    MetabolomeUnifiedCard
  } from './OmicsCards';
/**
 * Specialized component for rendering Step 2 data: Individual Metric Analysis
 */
const Step2 = ({ data }) => {
    // --- UPDATED DATA ACCESS ---
    // Access the specific arrays directly from the details object
    const standardMetrics = data?.details?.standardMetrics || [];
    const omicsAnalysis = data?.details?.omicsAnalysis || [];

    // Check if there's any data to display
    if (standardMetrics.length === 0 && omicsAnalysis.length === 0) {
        return <div className="s2-empty-state">No analysis data available</div>;
    }
    // --- END UPDATED DATA ACCESS ---


    // Status icon and color mapping (remains the same)
    const getStatusInfo = (status) => {
        switch (status?.toLowerCase()) { // Added toLowerCase for robustness
            case 'positive':
            case 'good': // Added aliases
            case 'processed': // Added alias for omics success
                return {
                    icon: faCircleCheck,
                    color: '#4caf50',
                    bgColor: 'rgba(76, 175, 80, 0.1)'
                };
            case 'negative':
            case 'poor': // Added alias
            case 'below target': // Added alias
                return {
                    icon: faCircleExclamation,
                    color: '#f44336',
                    bgColor: 'rgba(244, 67, 54, 0.1)'
                };
             case 'warning': // Explicitly handle Warning
                 return {
                     icon: faCircleExclamation, // Use exclamation for warning too
                     color: '#ff9800', // Orange color for warning
                     bgColor: 'rgba(255, 152, 0, 0.1)'
                 };
            case 'info':
            case 'none found': // Added alias for omics status
                return {
                    icon: faCircleInfo,
                    color: '#2196f3',
                    bgColor: 'rgba(33, 150, 243, 0.1)'
                };
            case 'neutral':
            case 'fair': // Added alias
            default: // Includes Error status from Omics if not explicitly handled
                // Check for specific error status
                 if (status?.toLowerCase() === 'error') {
                    return {
                        icon: faCircleExclamation, // Use exclamation for error
                        color: '#d32f2f', // Darker red for error
                        bgColor: 'rgba(211, 47, 47, 0.1)'
                    };
                 }
                // Default neutral
                return {
                    icon: faCircleMinus,
                    color: '#9e9e9e',
                    bgColor: 'rgba(158, 158, 158, 0.1)'
                };
        }
    };

    // --- UPDATED GROUPING ---
    // Group *standard* metrics by status type
    const groupedMetrics = {
        positive: standardMetrics.filter(m => getStatusInfo(m.status).icon === faCircleCheck) || [],
        negative: standardMetrics.filter(m => getStatusInfo(m.status).icon === faCircleExclamation && getStatusInfo(m.status).color === '#f44336') || [], // Only true negative
        warning: standardMetrics.filter(m => getStatusInfo(m.status).icon === faCircleExclamation && getStatusInfo(m.status).color === '#ff9800') || [], // Only warning
        neutral: standardMetrics.filter(m => getStatusInfo(m.status).icon === faCircleMinus) || [],
        info: standardMetrics.filter(m => getStatusInfo(m.status).icon === faCircleInfo) || [],
        error: standardMetrics.filter(m => getStatusInfo(m.status).color === '#d32f2f') || [] // Errors in standard metrics if they occur
    };
    // --- END UPDATED GROUPING ---


    // Calculate position for the marker on the range bar (remains the same)
    const getMarkerPosition = (metric) => {
        // ... (no changes needed in this function's logic) ...
        // Default position in middle if we can't determine
        let position = '50%';

        // Different logic based on interpretation and target format
        if (metric.target && typeof metric.value === 'number') {
            if (metric.target.includes('-')) {
                // For range targets like "18.50 - 24.90"
                const [min, max] = metric.target.split('-').map(v => parseFloat(v.trim()));
                if (!isNaN(min) && !isNaN(max) && max > min) { // Added check max > min
                    const range = max - min;
                    // Handle values outside the range
                    if (metric.value < min) {
                        position = '0%';
                    } else if (metric.value > max) {
                        position = '100%';
                    } else {
                        // Calculate percentage within range
                        const percentage = ((metric.value - min) / range) * 100;
                        position = `${percentage}%`;
                    }
                }
            } else if (metric.target.includes('>=')) {
                // For minimum thresholds like ">= 7500.00"
                const threshold = parseFloat(metric.target.replace('>=', '').trim());
                if (!isNaN(threshold)) {
                    if (metric.value >= threshold) {
                        // Position relative to threshold, maybe cap at 100%? Let's scale up to threshold = 50%
                         const scaleFactor = 2; // How much higher than threshold counts as "max" visually
                         const relativeValue = Math.min(scaleFactor, metric.value / threshold); // Cap at 2x threshold
                         position = `${50 + (relativeValue - 1) * 50}%`; // Scales from 50% (at threshold) to 100% (at 2x threshold)

                    } else {
                        // Calculate how far below threshold (0 to 50%)
                        const ratio = Math.max(0, metric.value / threshold);
                        position = `${ratio * 50}%`;
                    }
                }
            } else if (metric.target.includes('<=')) {
                // For maximum thresholds like "<= 50.00"
                 const threshold = parseFloat(metric.target.replace('<=', '').trim());
                 if (!isNaN(threshold)) {
                     if (metric.value <= threshold) {
                         // Position relative to threshold, threshold = 50%
                         const ratio = Math.max(0, metric.value / threshold); // Cap at 0
                         position = `${ratio * 50}%`; // Scales from 0% to 50% (at threshold)
                     } else {
                         // Calculate how far above threshold (50% to 100%)
                         const scaleFactor = 2; // How much higher than threshold counts as "max" visually
                         const relativeValue = Math.min(scaleFactor, metric.value / threshold); // Cap at 2x threshold
                         position = `${50 + (relativeValue - 1) * 50}%`; // Scales from 50% (at threshold) to 100% (at 2x threshold)
                     }
                 }
            } else if (metric.target.includes('~')) {
                 // Handle approximate targets e.g., "~ 2.00" - treat as center
                 position = '50%';
            }
        } else if (metric.interpretation) {
            // Position based on interpretation if no numeric values available
            if (metric.interpretation.toLowerCase().includes('above')) {
                position = '85%';
            } else if (metric.interpretation.toLowerCase().includes('below')) {
                position = '15%';
            } else if (metric.interpretation.toLowerCase().includes('within')) {
                position = '50%';
            }
        }

        return position;
    };

    // Render a standard metric card (remains mostly the same)
    const renderMetricCard = (metric, index) => {
        const statusInfo = getStatusInfo(metric.status);

        // Format the value if it exists
        let formattedValue = 'N/A';
        if (metric.value !== undefined && metric.value !== null) {
            formattedValue = typeof metric.value === 'number'
                ? metric.value.toLocaleString(undefined, { maximumFractionDigits: 1 })
                : String(metric.value); // Ensure it's a string
        } else if (metric.unit && !String(metric.unit).includes('N/A')) { // Ensure unit is string
            // For metrics that don't have a value but have a unit, value might be implied
            formattedValue = "--";
        }

        return (
            <div
                className="s2-metric-card"
                key={`${metric.metric}-${index}`} // Use index for potentially non-unique metric names if rules repeat
                style={{
                    borderLeft: `0.4vh solid ${statusInfo.color}`,
                    animationDelay: `${0.1 + (index * 0.05)}s`
                }}
            >
                <div className="s2-metric-header">
                    <div className="s2-metric-title">
                        <FontAwesomeIcon
                            icon={statusInfo.icon}
                            className="s2-status-icon"
                            style={{ color: statusInfo.color }}
                        />
                        <span>{metric.metric}</span>
                    </div>
                    <div className="s2-metric-interpretation" style={{ color: statusInfo.color }}>
                        {metric.interpretation}
                    </div>
                </div>

                <div className="s2-metric-compact">
                    <div className="s2-metric-values">
                        <div className="s2-value-container">
                        <div className="s2-value-label">Current</div>

                            <div className="s2-value">
                                {formattedValue}
                                {metric.unit && !String(metric.unit).includes('N/A') && <span className="s2-unit">{metric.unit}</span>}
                            </div>
                        </div>

                        <div className="s2-divider"></div>

                        <div className="s2-target-container">
                             {/* Ensure target is displayable */}
                            <div className="s2-target">{metric.target !== undefined && metric.target !== null ? String(metric.target) : 'N/A'}</div>
                            <div className="s2-value-label">
                                <FontAwesomeIcon icon={faBullseye} className="s2-target-icon" />
                                Target
                            </div>
                        </div>
                    </div>

                    {/* Range visualization bar */}
                    <div className="s2-range-bar-container">
                        <div
                            className={`s2-range-bar s2-range-${metric.status?.toLowerCase()}`} // Added safe navigation
                        >
                            {/* For range-based targets, show the target zone */}
                            {metric.target && String(metric.target).includes('-') && ( // Ensure target is string
                                <div className="s2-target-zone"></div>
                            )}
                            {/* Position marker based on interpretation */}
                            <div
                                className="s2-value-marker"
                                style={{
                                    left: getMarkerPosition(metric),
                                    backgroundColor: statusInfo.color
                                }}
                            ></div>
                        </div>
                    </div>
                    {metric.applied_rule_conditions && metric.applied_rule_conditions.length > 0 && (
                        <div className="s2-condition-bubbles">
                            {/* Group conditions by feature */}
                            {Object.entries(metric.applied_rule_conditions.reduce((grouped, condition) => {
                                if (!grouped[condition.feature]) {
                                    grouped[condition.feature] = [];
                                }
                                grouped[condition.feature].push(condition);
                                return grouped;
                            }, {})).map(([feature, conditions]) => (
                                <div key={feature} className="s2-condition-bubble">
                                    {feature}
                                    <div className="s2-hover-details">
                                        <div className="s2-detail-header">{feature}</div>
                                        {conditions.map((condition, i) => (
                                            <div key={i} className="s2-detail-row">
                                                {condition.op} {condition.value}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>


                <div className="s2-metric-details">
                <div className="s2-metric-footer">
                    <div className="s2-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-source-icon" />
                        {metric.source}
                    </div>

                </div>
                    {metric.rule_type && (
                        <div className="s2-detail-row">
                            <span className="s2-detail-label">Rule Type:</span>
                            <span className="s2-detail-value">{metric.rule_type}</span>
                        </div>
                    )}
                    {metric.notes && (
                        <div className="s2-detail-row">
                            <span className="s2-detail-label">Notes:</span>
                            <span className="s2-detail-value">{metric.notes}</span>
                        </div>
                    )}
                    {metric.missing_data_notes && (
                        <div className="s2-detail-row-bottom">
                            <span className="s2-detail-value">{metric.missing_data_notes}</span>
                        </div>
                    )}

                </div>
            </div>
        );
    };

    // --- UPDATED OMICS CARD RENDERER ---
    // Render an omics card using specialized components based on metric type
    const renderOmicsCard = (metric, index) => {
        // Debug logging
        console.log(`Rendering omics card for: ${metric.metric}`, metric);
        
        // Clean up metric name for mapping
        const metricType = metric.metric?.toLowerCase().trim() || '';
        
        // Create proper data structure for omics cards
        // The omics card components expect { data: { details: {...} } } structure
        const data = {
            ...metric,  // Keep all original properties (value, status, interpretation, etc.)
            details: metric.details || {} // Make sure details exists
        };
        
        console.log(`Card type: ${metricType}, has details:`, !!metric.details);
        
        // Key with animation delay for all cards
        const commonProps = {
            key: `${metric.metric || 'omics'}-${index}`,
            data: data, // Pass data properly as the component expects
            animationDelay: `${0.1 + (index * 0.05)}s`
        };
        
        // Map metric types to their specialized components
        if (metricType.includes('wgs') || metricType.includes('whole genome')) {
            console.log('Rendering WGS Analysis Card');
            return <WgsAnalysisCard {...commonProps} />;
        } else if (metricType.includes('gut health') || metricType.includes('microbiome')) {
            console.log('Rendering Gut Health Card');
            return <GutHealthCard {...commonProps} />;
        } else if (metricType.includes('proteomics')) {
            console.log('Rendering Proteomics Card');
            return <ProteomicsCard {...commonProps} />;
        } else if (metricType.includes('oral health')) {
            console.log('Rendering Oral Health Card');
            return <OralHealthCard {...commonProps} />;
        } else if (metricType.includes('transcriptome') || metricType.includes('rna')) {
            console.log('Rendering Transcriptome Card');
            return <TranscriptomeCard {...commonProps} />;
        } else if (metricType.includes('metabolome') && metricType.includes('plasma')) {
            console.log('Rendering Metabolome Plasma Card');
            return <MetabolomeUnifiedCard {...commonProps} type="plasma" />;
        } else if (metricType.includes('metabolome') && metricType.includes('urine')) {
            console.log('Rendering Metabolome Urine Card');
            return <MetabolomeUnifiedCard {...commonProps} type="urine" />;
        } else if (metricType.includes('lipidome')) {
            console.log('Rendering Metabolome Lipids Card');
            return <MetabolomeUnifiedCard {...commonProps} type="lipids" />;
        } else {
            // Fallback to a generic rendering for any unmatched metric types
            console.log('Rendering generic card (no specialized component found)');
            const statusInfo = getStatusInfo(metric.status);
            const title = metric.metric?.replace('Analysis Summary', '').replace('Summary', '').trim() || 'Omics Result';
            
            return (
                <div
                    className="s2-omics-card"
                    key={commonProps.key}
                    style={{
                        borderLeft: `0.4vh solid ${statusInfo.color}`,
                        animationDelay: commonProps.animationDelay
                    }}
                >
                    <div className="s2-omics-header">
                        <FontAwesomeIcon
                            icon={statusInfo.icon}
                            className="s2-status-icon s2-omics-status-icon"
                            style={{ color: statusInfo.color }}
                        />
                        <div className="s2-omics-title">{title}</div>
                    </div>
                    <div className="s2-omics-content">
                        {metric.value && metric.value !== "N/A" && metric.value !== "Placeholder" && !metric.value?.includes('Placeholder') && (
                            <div className="s2-omics-value">{String(metric.value)}</div>
                        )}
                        {metric.interpretation && (
                            <div className="s2-omics-interpretation" style={{ color: statusInfo.color }}>
                                {metric.interpretation}
                            </div>
                        )}
                        {metric.notes && (
                            <div className="s2-omics-notes">Notes: {metric.notes}</div>
                        )}
                        {metric.source && (
                            <div className="s2-omics-source">{metric.source}</div>
                        )}
                    </div>
                </div>
            );
        }
    };
    // --- END UPDATED OMICS CARD RENDERER ---

    // --- UPDATED RENDER SECTIONS ---
    return (
        <div className="s2-container">
            {/* Positive Metrics */}
            {groupedMetrics.positive.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleCheck}
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Positive').color }}
                        />
                        <span>Positive Findings</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.positive.map(renderMetricCard)}
                    </div>
                </div>
            )}

            {/* Negative Metrics (Areas of Concern) */}
            {groupedMetrics.negative.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleExclamation}
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Negative').color }}
                        />
                        <span>Areas of Concern</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.negative.map(renderMetricCard)}
                    </div>
                </div>
            )}

             {/* Warning Metrics */}
            {groupedMetrics.warning.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleExclamation} // Warning uses same icon as negative but different color
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Warning').color }}
                        />
                        <span>Warnings</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.warning.map(renderMetricCard)}
                    </div>
                </div>
            )}

            {/* Neutral Metrics */}
            {groupedMetrics.neutral.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleMinus}
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Neutral').color }}
                        />
                        <span>Neutral Findings</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.neutral.map(renderMetricCard)}
                    </div>
                </div>
            )}

            {/* Info Metrics */}
            {groupedMetrics.info.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Info').color }}
                        />
                        <span>Information</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.info.map(renderMetricCard)}
                    </div>
                </div>
            )}

             {/* Error Metrics (if any standard metrics failed) */}
             {groupedMetrics.error.length > 0 && (
                <div className="s2-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faCircleExclamation}
                            className="s2-section-icon"
                            style={{ color: getStatusInfo('Error').color }}
                        />
                        <span>Processing Errors</span>
                    </div>
                    <div className="s2-cards-grid">
                        {groupedMetrics.error.map(renderMetricCard)} {/* Or a dedicated error card */}
                    </div>
                </div>
             )}


            {/* Omics Analysis Section - Use the omicsAnalysis array directly */}
            {omicsAnalysis.length > 0 && (
                <div className="s2-section s2-omics-section">
                    <div className="s2-section-header">
                        <FontAwesomeIcon
                            icon={faNotesMedical}
                            className="s2-section-icon"
                            style={{ color: '#64b5f6' }}
                        />
                        <span>Omics Analysis</span>
                    </div>
                    <div className="s2-omics-grid">
                        {/* Map directly over the omicsAnalysis array */}
                        {omicsAnalysis.map(renderOmicsCard)}
                    </div>
                </div>
            )}
        </div>
    );
    // --- END UPDATED RENDER SECTIONS ---
};

export default Step2;